import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import carouselNext from '../Menu/SVGs/FootItemCarouselNextButton.svg';
import carouselBack from '../Menu/SVGs/FootItemCarouselBackButton.svg';
import restaurantLogo from '../Menu/SVGs/RestaurantDummyLogo.svg';
import dietaryLogo from '../Menu/SVGs/DietaryDummyLogo.svg';
import Skeleton from 'react-loading-skeleton';
import parse from 'html-react-parser';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../../Common/HeaderFooter/Header';
import SignUpWidget from '../Signup/SignUpWidget';
import Login from '../Login/Login';
import { getPricesToShown } from '../../Utilities/getPricesToShown';
import { getloggedInUser } from '../../Utilities/getloggedInUser';
import { LocalStorageCompany } from '../../Interfaces/PayloadsAndResponses/Auth';
import './weekdayOrder.css';
import WeekdayOrderAssistant, { SuggestedLine } from './WeekdayOrderAssistant';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_API_KEY || '');
const CART_KEY = 'weekdayOrderCart';

type PlacedReceipt = {
  when: string;
  location: string;
  instructions: string;
  itemCount: number;
  food: number;
  salesTax: number;
  service: number;
  cardFee: number;
  total: number;
};

type Restaurant = {
  address_id: number;
  restaurant_id: number;
  name: string;
  position: number;
  cuisine: string;
  rating: number;
  logo?: string | { url?: string | null };
};

type FoodImage = { url?: string; medium?: { url?: string } };

type OptionChoice = { id: number; description: string; price: number };
type OptionSet = { id: number; name: string; required?: number | boolean; start_limit?: number; end_limit?: number; options?: OptionChoice[] };
type Nutrition = { name: string; value: number; childs?: { name: string; value: number }[] };

type FoodItem = {
  id: number;
  name: string;
  description?: string;
  price: number;
  image?: string | FoodImage;
  dietaries?: { name: string; logo?: string; enable_user_to_filter?: boolean }[];
  weighted_rating?: number;
  average_rating?: number;
  rating_count?: number;
  best_seller?: boolean;
  skip_markup?: boolean;
  restaurant_name?: string;
  optionsets?: OptionSet[];
  nutritions?: Nutrition[];
  sales_tax?: number;
};

type ChosenOption = { optionset_id: number; option_id: number; name: string; price: number };

type MenuSection = { id?: number; name?: string; fooditems?: FoodItem[] };

type MenuRestaurant = {
  id?: number;
  name: string;
  address_id?: number;
  logo_url?: string;
  sections?: MenuSection[];
};

type PlacedItem = { item: FoodItem; restaurant: Restaurant };

type MenuRow = {
  key: string;
  title: string;
  eyebrow?: string;
  logo?: string;
  icon?: string;
  anchor?: string;
  items: PlacedItem[];
};

type CartLine = {
  fooditemId: number;
  name: string;
  price: number;
  quantity: number;
  restaurantAddressId: number;
  restaurantName: string;
  image?: string;
  utensils?: boolean;
  options?: ChosenOption[];
  salesTaxRate?: number;
};

type StoredCart = { day: string; lines: CartLine[] };
type AddressPick = {
  id?: number;
  address_line: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  time_zone: string;
  suite_no?: string;
  street_number?: string;
  street?: string;
};

const WINDOWS = Array.from({ length: 17 }, (_, index) => {
  const startMin = 11 * 60 + index * 30;
  const endMin = startMin + 30;
  const label = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const suffix = h >= 12 ? 'pm' : 'am';
    const hour = h % 12 === 0 ? 12 : h % 12;
    return `${hour}:${m.toString().padStart(2, '0')}${suffix}`;
  };
  return { value: `${String(Math.floor(startMin / 60)).padStart(2, '0')}:${String(startMin % 60).padStart(2, '0')}`, label: `${label(startMin)}–${label(endMin)}` };
});

function authHeaders() {
  try {
    return JSON.parse(localStorage.getItem('headers') || '{}');
  } catch {
    return {};
  }
}

function signedIn() {
  const headers = authHeaders();
  return Boolean(headers['access-token'] && headers.uid);
}

function weekdayOrderAllowed() {
  if (!signedIn()) return true;
  return getloggedInUser().isCompanyAdmin;
}

function loadCart(): StoredCart {
  try {
    const parsed = JSON.parse(localStorage.getItem(CART_KEY) || '');
    if (parsed?.day && Array.isArray(parsed.lines)) return parsed;
  } catch {
    // empty cart
  }
  return { day: '', lines: [] };
}

function imageUrl(image?: string | FoodImage) {
  if (!image) return '';
  if (typeof image === 'string') return image;
  return image.medium?.url || image.url || '';
}

const SERVICE_FEE_RATE = 0.15;
const MINIMUM_ORDER = 199;

function money(value: number) {
  return `$${value.toFixed(2)}`;
}

function orderFees(subtotal: number) {
  const service = Math.round(subtotal * SERVICE_FEE_RATE * 100) / 100;
  const shortOfMinimum = Math.max(0, Math.round((MINIMUM_ORDER - subtotal) * 100) / 100);
  return { service, shortOfMinimum, meetsMinimum: shortOfMinimum === 0 };
}

function chargeSummary(lines: CartLine[], includeCardFee: boolean) {
  const subtotal = Math.round(lines.reduce((sum, line) => sum + line.price * line.quantity, 0) * 100) / 100;
  const fees = orderFees(subtotal);
  const salesTax = Math.round(lines.reduce((sum, line) => sum + line.price * line.quantity * (Number(line.salesTaxRate) || 0), 0) * 100) / 100;
  const beforeCard = Math.round((subtotal + fees.service + salesTax) * 100) / 100;
  let cardFee = 0;
  if (includeCardFee && beforeCard > 0) {
    const charged = Math.round(((beforeCard + 0.30) / (1 - 0.029)) * 100) / 100;
    cardFee = Math.round((charged - beforeCard) * 100) / 100;
  }
  return { subtotal, ...fees, salesTax, cardFee, total: Math.round((beforeCard + cardFee) * 100) / 100 };
}

function storedCompany() {
  try {
    const company = JSON.parse(localStorage.getItem('company') || '{}') as LocalStorageCompany;
    return company && Object.keys(company).length ? company : null;
  } catch {
    return null;
  }
}

function roundMenu(num: number | string) {
  const numberInput = parseFloat(`${num}`);
  if (!Number.isFinite(numberInput)) return 0;
  if (numberInput % 1 === 0) return numberInput;
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(numberInput);
}

function mealBudget(company: NonNullable<ReturnType<typeof storedCompany>>) {
  const budget = parseFloat(`${company.user_meal_budget}`);
  return Number.isFinite(budget) ? budget : 0;
}

function optionMinimum(set: OptionSet) {
  const requiredCount = typeof set.required === 'number' ? set.required : (set.required ? 1 : 0);
  return Math.max(requiredCount, Number(set.start_limit) || 0);
}

function meetsOptionRules(item: FoodItem, options?: { optionset_id: number }[]) {
  return (item.optionsets || []).every(set => {
    const minimum = optionMinimum(set);
    if (minimum < 1) return true;
    return (options || []).filter(option => option.optionset_id === set.id).length >= minimum;
  });
}

function markedUpPrice(item: FoodItem, optionExtra = 0) {
  return baseShown(item) + (optionExtra || 0);
}

function baseShown(item: FoodItem) {
  const company = storedCompany();
  const base = Number(item.price) || 0;
  if (!company) return base;
  const shown = Number(getPricesToShown(base, Boolean(item.skip_markup), company, mealBudget(company)));
  return Number.isFinite(shown) ? shown : base;
}

function employeeCardPaid(price: number, company: NonNullable<ReturnType<typeof storedCompany>>) {
  if (!(company.user_copay && parseFloat(`${company.copay_amount}`) >= 0)) return '';
  const copay = parseFloat(`${company.copay_amount}`) || 0;
  let remaining = mealBudget(company);
  if (remaining < 0) remaining = 0;
  let paid = 0;
  if (remaining >= price) paid = copay;
  else {
    paid = price - remaining;
    if (remaining > 0 && remaining > copay) paid += copay;
    else paid = price;
    if (paid < copay) paid = price;
  }
  const afterCopay = Number(paid).toFixed(2) === '0.00' ? '' : Number(paid).toFixed(2);
  return afterCopay ? `$${((+afterCopay + 0.3) / (1 - 0.029)).toFixed(2)}` : '';
}

function cardPriceLabel(item: FoodItem) {
  const company = storedCompany();
  if (!company || company.self_signup) return `$${item.price}`;
  const { isCompanyAdmin, isUnsubsidizedUser } = getloggedInUser();
  const shown = baseShown(item);
  if (isCompanyAdmin) return shown ? `$${roundMenu(shown)}` : '';
  if (isUnsubsidizedUser) {
    const withFee = ((+shown + 0.3) / (1 - 0.029)).toFixed(2);
    return withFee ? `$${withFee}` : '';
  }
  return employeeCardPaid(shown, company);
}

function lineKey(line: Pick<CartLine, 'fooditemId' | 'utensils' | 'options'>) {
  const options = (line.options || []).map(option => option.option_id).sort().join(',');
  return `${line.fooditemId}:${options}:${line.utensils ? 1 : 0}`;
}

function itemHasPhoto(item: FoodItem) {
  if (!item.image) return false;
  if (typeof item.image === 'string') return Boolean(item.image);
  return Boolean(item.image.medium?.url);
}

function groupSlides(items: PlacedItem[], keepSortedOrder: boolean) {
  if (!keepSortedOrder) {
    const withImage = items.filter(entry => itemHasPhoto(entry.item));
    const withoutImage = items.filter(entry => !itemHasPhoto(entry.item));
    if (!withImage.length) return items.map(entry => [entry]);
    const slides = withImage.map(entry => [entry]);
    for (let index = 0; index < withoutImage.length; index += 2) slides.push(withoutImage.slice(index, index + 2));
    return slides;
  }
  const slides: PlacedItem[][] = [];
  for (let index = 0; index < items.length; index += 1) {
    const current = items[index];
    const next = items[index + 1];
    if (!itemHasPhoto(current.item) && next && !itemHasPhoto(next.item)) {
      slides.push([current, next]);
      index += 1;
    } else {
      slides.push([current]);
    }
  }
  return slides;
}

function Stars({ rating }: { rating?: number }) {
  const value = Number(rating) || 0;
  if (!value) return null;
  return (
    <div className="wo-rate" aria-label={`${value.toFixed(1)} stars`}>
      {[1, 2, 3, 4, 5].map(star => {
        const fill = value >= star ? 100 : value > star - 1 ? Math.round((value - (star - 1)) * 100) : 0;
        return <span className="wo-rate-star" key={star}><span style={{ width: `${fill}%` }} /></span>;
      })}
    </div>
  );
}

function MenuSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading the menu">
      {[0, 1].map(section => (
        <section className="wo-band" key={section}>
          <div className="wo-section-head">
            <Skeleton circle width={55} height={55} />
            <Skeleton width={148} height={22} />
          </div>
          <div className="wo-hrow">
            {[0, 1, 2, 3].map(card => (
              <div className="wo-slide" key={card}>
                <div className="wo-card wo-card-skeleton">
                  <Skeleton width="100%" height={144} borderRadius={8} />
                  <div className="wo-card-body">
                    <div className="wo-card-top">
                      <Skeleton width="70%" height={18} />
                      <Skeleton width={48} height={18} />
                    </div>
                    <Skeleton width={110} height={12} />
                    <Skeleton width={92} height={14} />
                    <Skeleton width="100%" height={32} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function HScroll({ children, chip, photo }: { children: React.ReactNode; chip?: boolean; photo?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ left: false, right: false });
  const update = () => {
    const el = ref.current;
    if (!el) return;
    setEnds({
      left: el.scrollLeft > 8,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 8
    });
  };
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [children]);
  return (
    <div className="wo-hscroll">
      {ends.left && <button type="button" className={`wo-arrow left ${chip ? 'chips' : ''} ${photo ? 'photo' : ''}`} aria-label="Scroll left" onClick={() => ref.current?.scrollBy({ left: -320, behavior: 'smooth' })}>{photo ? <img src={carouselBack} alt="" /> : <i className="fas fa-chevron-left" />}</button>}
      <div className="wo-hrow" ref={ref} onScroll={update}>{children}</div>
      {ends.right && <button type="button" className={`wo-arrow right ${chip ? 'chips' : ''} ${photo ? 'photo' : ''}`} aria-label="Scroll right" onClick={() => ref.current?.scrollBy({ left: 320, behavior: 'smooth' })}>{photo ? <img src={carouselNext} alt="" /> : <i className="fas fa-chevron-right" />}</button>}
    </div>
  );
}

type GooglePlace = {
  geometry?: { location?: { lat: () => number; lng: () => number } };
  address_components?: { types: string[]; short_name: string; long_name?: string }[];
  formatted_address?: string;
};

function placeToAddress(place: GooglePlace): AddressPick | null {
  if (!place.geometry?.location || !place.address_components) return null;
  const part = (type: string, name: 'short_name' | 'long_name' = 'short_name') => place.address_components?.find(component => component.types.includes(type))?.[name] || '';
  return {
    address_line: place.formatted_address || '',
    city: part('locality', 'long_name') || part('sublocality', 'long_name') || part('administrative_area_level_2', 'long_name'),
    state: part('administrative_area_level_1'),
    zip: part('postal_code'),
    latitude: place.geometry.location.lat(),
    longitude: place.geometry.location.lng(),
    time_zone: 'America/Los_Angeles',
    suite_no: part('subpremise'),
    street_number: part('street_number'),
    street: part('route', 'long_name')
  };
}

let releaseAddressWidget: (() => void) | null = null;

function bindAddressInput(inputRef: React.RefObject<HTMLInputElement>, onPick: (next: AddressPick) => void) {
  releaseAddressWidget?.();
  let timer = 0;
  let alive = true;
  let bound: HTMLInputElement | null = null;
  let autocomplete: { addListener: (event: string, handler: () => void) => void; getPlace: () => GooglePlace } | null = null;
  const googleMaps = () => (window as unknown as { google?: { maps?: { event?: { clearInstanceListeners: (target: object) => void }; places?: { Autocomplete: new (input: HTMLInputElement, opts: object) => NonNullable<typeof autocomplete> } } } }).google;
  const release = () => {
    if (!alive) return;
    alive = false;
    window.clearTimeout(timer);
    const maps = googleMaps()?.maps;
    if (autocomplete && maps?.event) maps.event.clearInstanceListeners(autocomplete);
    if (bound && maps?.event) maps.event.clearInstanceListeners(bound);
    if (bound) {
      bound.disabled = false;
      bound.readOnly = false;
      bound.classList.remove('pac-target-input', 'gm-err-autocomplete');
      if ((bound.placeholder || '').startsWith('Oops')) bound.placeholder = 'Delivery address';
    }
    document.querySelectorAll('.pac-container').forEach(node => node.remove());
    if (releaseAddressWidget === release) releaseAddressWidget = null;
  };
  releaseAddressWidget = release;
  const bind = () => {
    if (!alive) return;
    const places = googleMaps()?.maps?.places;
    if (!inputRef.current || !places) {
      timer = window.setTimeout(bind, 400);
      return;
    }
    bound = inputRef.current;
    bound.disabled = false;
    bound.readOnly = false;
    if ((bound.placeholder || '').startsWith('Oops')) bound.placeholder = 'Delivery address';
    autocomplete = new places.Autocomplete(bound, { types: ['address'], componentRestrictions: { country: 'us' }, fields: ['address_components', 'geometry', 'formatted_address'] });
    autocomplete.addListener('place_changed', () => {
      const next = placeToAddress(autocomplete?.getPlace() || {});
      if (next) onPick(next);
    });
  };
  bind();
  return release;
}

function earliestDate(zone: string) {
  const now = dayjs().tz(zone);
  const afterCutoff = now.hour() > 14 || (now.hour() === 14 && now.minute() >= 0);
  return (afterCutoff ? now.add(2, 'day') : now.add(1, 'day')).format('YYYY-MM-DD');
}

function deliveryMoment(date: string, windowStart: string, zone: string) {
  return dayjs.tz(`${date}T${windowStart}:00`, zone);
}

function deliveryInstant(date: string, windowStart: string, zone: string) {
  return deliveryMoment(date, windowStart, zone).add(30, 'minute').format('YYYY-MM-DDTHH:mm:ssZ');
}

function zoneAbbr(when: dayjs.Dayjs, zone: string) {
  return new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
    .formatToParts(when.toDate())
    .find(part => part.type === 'timeZoneName')?.value || '';
}

function deliveryLabel(date: string, windowStart: string, zone: string) {
  const when = deliveryMoment(date, windowStart, zone);
  return `${when.format('ddd. MMM D h:mmA')} ${zoneAbbr(when, zone)}`;
}

function cutoffMoment(date: string, zone: string) {
  return dayjs.tz(`${date}T14:00:00`, zone).subtract(1, 'day');
}

function cutoffCopy(date: string, zone: string) {
  const cutoff = cutoffMoment(date, zone);
  if (cutoff.isAfter(dayjs())) return { past: false, text: `Cutoff in ${cutoff.toNow(true)}` };
  return { past: true, text: `Cutoff was ${cutoff.fromNow()}` };
}

function logoSrc(logo?: string | { url?: string | null }) {
  if (!logo) return '';
  if (typeof logo === 'string') return logo.startsWith('http') || logo.startsWith('/') ? logo : '';
  return logo.url || '';
}

function restaurantMark(restaurant: Restaurant, menu?: MenuRestaurant) {
  const uploaded = logoSrc(menu?.logo_url) || logoSrc(restaurant.logo);
  if (uploaded) return uploaded;
  for (const section of menu?.sections || []) {
    for (const item of section.fooditems || []) {
      const photo = imageUrl(item.image);
      if (photo) return photo;
    }
  }
  return restaurantLogo;
}

function filterIcon(title: string, icon?: string) {
  if (icon && !icon.trim().startsWith('<')) return icon;
  const key = title.toLowerCase();
  if (key === 'most popular') return '/imgs/SVGs/Most Popular.svg';
  if (key === 'vegetarian') return '/imgs/SVGs/bowl-food-duotone.svg';
  if (key === 'vegan') return '/imgs/SVGs/pot.svg';
  if (key === 'gluten free') return '/imgs/SVGs/soup.svg';
  if (key === 'under $15') return '/imgs/SVGs/store-duotone.svg';
  return dietaryLogo;
}

function FilterMark({ icon, title, className }: { icon?: string; title: string; className?: string }) {
  const markup = icon?.trim().startsWith('<') ? icon.trim() : '';
  if (markup) return <span className={`wo-mark ${className || ''}`} aria-hidden="true">{parse(markup)}</span>;
  return <img className={className} src={filterIcon(title, icon)} alt="" />;
}

function itemDietaries(item: FoodItem) {
  return (item.dietaries || []).map(dietary => dietary.name.toLowerCase());
}

function WeekdayOrder() {
  const zoneRef = useRef('America/Los_Angeles');
  const [timeZone, setTimeZone] = useState('America/Los_Angeles');
  const [date, setDate] = useState(() => earliestDate('America/Los_Angeles'));
  const [windowStart, setWindowStart] = useState('11:30');
  const [address, setAddress] = useState<AddressPick | null>(null);
  const [savedAddresses, setSavedAddresses] = useState<AddressPick[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [menus, setMenus] = useState<MenuRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [budget, setBudget] = useState(40);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<{ item: FoodItem; restaurant: Restaurant; preset?: SuggestedLine } | null>(null);
  const [editing, setEditing] = useState<CartLine | null>(null);
  const [sort, setSort] = useState<'recommended' | 'rating' | 'price'>('recommended');
  const [cart, setCart] = useState<StoredCart>(loadCart);
  const [pendingAdd, setPendingAdd] = useState<CartLine | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [payBy, setPayBy] = useState<'invoice' | 'credit_card' | null>(signedIn() ? null : 'credit_card');
  const [placed, setPlaced] = useState(false);
  const [receipt, setReceipt] = useState<PlacedReceipt | null>(null);
  const addressInput = useRef<HTMLInputElement>(null);
  const chooseAddressRef = useRef<(next: AddressPick) => void>(() => undefined);
  const datePopRef = useRef<HTMLDivElement>(null);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  useEffect(() => {
    if (!dateOpen) return undefined;
    const close = (event: MouseEvent) => {
      if (!datePopRef.current?.contains(event.target as Node)) setDateOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [dateOpen]);

  async function chooseAddress(next: AddressPick) {
    try {
      const zone = await axios.get('/searches/time_zone', { params: { latitude: next.latitude, longitude: next.longitude } });
      next.time_zone = zone.data.time_zone || next.time_zone;
    } catch {
      next.time_zone = 'America/Los_Angeles';
    }
    zoneRef.current = next.time_zone;
    setTimeZone(next.time_zone);
    setDate(current => {
      const earliest = earliestDate(next.time_zone);
      return current < earliest ? earliest : current;
    });
    setAddress(next);
    if (addressInput.current) addressInput.current.value = next.address_line;
  }
  chooseAddressRef.current = chooseAddress;

  useEffect(() => {
    if (checkoutOpen) return undefined;
    return bindAddressInput(addressInput, next => chooseAddressRef.current(next));
  }, [checkoutOpen]);

  useEffect(() => {
    if (!signedIn() || !weekdayOrderAllowed()) return;
    axios.get('/companies/addresses', { headers: authHeaders() }).then(response => {
      const rows = ((response.data?.addresses || []) as Array<AddressPick & { formatted_address?: string }>).flatMap(row => {
        const latitude = Number(row.latitude);
        const longitude = Number(row.longitude);
        if (!row.address_line || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
        return [{
          id: row.id,
          address_line: row.address_line,
          city: row.city || '',
          state: row.state || '',
          zip: row.zip || '',
          latitude,
          longitude,
          time_zone: row.time_zone || 'America/Los_Angeles',
          suite_no: row.suite_no,
          street_number: row.street_number,
          street: row.street
        }];
      });
      setSavedAddresses(rows);
      const user = JSON.parse(localStorage.getItem('user') || '{}') as { address_id?: number };
      const match = rows.find(row => row.id === user.address_id) || (rows.length === 1 ? rows[0] : undefined);
      if (match) chooseAddressRef.current(match);
    }).catch(() => undefined);
    axios.get('/billings', { headers: authHeaders() }).then(response => {
      const method = response.data?.invoice_credit_card;
      setPayBy(method === 'invoice' ? 'invoice' : 'credit_card');
    }).catch(() => setPayBy('credit_card'));
  }, []);

  useEffect(() => {
    if (!weekdayOrderAllowed()) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get('/weekday_combos', { params: { date, time_zone: timeZone } })
      .then(async response => {
        if (cancelled) return;
        const list: Restaurant[] = response.data.restaurants || [];
        setRestaurants(list);
        if (!list.length) {
          setMenus([]);
          return;
        }
        const menusResponse = await axios.get('/addresses/menus', {
          params: { menu_type: 1, address_ids: list.map(restaurant => restaurant.address_id) },
          headers: authHeaders()
        });
        if (!cancelled) setMenus(menusResponse.data.restaurants || []);
      })
      .catch(err => {
        if (!cancelled) {
          setRestaurants([]);
          setMenus([]);
          setError(err?.response?.data?.message || 'No menu for that date.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, timeZone]);

  useEffect(() => {
    const rates = new Map<number, number>();
    menus.forEach(menu => {
      (menu.sections || []).forEach(section => {
        (section.fooditems || []).forEach(item => {
          if (item.sales_tax != null) rates.set(item.id, Number(item.sales_tax) || 0);
        });
      });
    });
    if (!rates.size) return;
    setCart(current => {
      let changed = false;
      const lines = current.lines.map(line => {
        if (line.salesTaxRate != null) return line;
        const rate = rates.get(line.fooditemId);
        if (rate == null) return line;
        changed = true;
        return { ...line, salesTaxRate: rate };
      });
      return changed ? { ...current, lines } : current;
    });
  }, [menus]);

  const menuRows = useMemo(() => {
    const groups = restaurants.map(restaurant => ({
      restaurant,
      menu: menus.find(entry => entry.address_id === restaurant.address_id || entry.name === restaurant.name)
    }));
    const needle = query.trim().toLowerCase();
    const accept = (item: FoodItem, restaurant: Restaurant) => {
      if (markedUpPrice(item) > budget) return false;
      if (!needle) return true;
      return item.name.toLowerCase().includes(needle)
        || (item.description || '').toLowerCase().includes(needle)
        || restaurant.name.toLowerCase().includes(needle)
        || (item.dietaries || []).some(dietary => (dietary.name || '').toLowerCase().includes(needle));
    };
    const sortItems = (items: PlacedItem[]) => {
      const next = items.slice();
      if (sort === 'rating') next.sort((a, b) => (Number(b.item.average_rating || b.item.weighted_rating) || 0) - (Number(a.item.average_rating || a.item.weighted_rating) || 0));
      else if (sort === 'price') next.sort((a, b) => markedUpPrice(a.item) - markedUpPrice(b.item));
      return next;
    };
    const all: PlacedItem[] = [];
    groups.forEach(({ restaurant, menu }) => {
      (menu?.sections || []).forEach(section => {
        (section.fooditems || []).forEach(item => {
          if (accept(item, restaurant)) all.push({ item, restaurant });
        });
      });
    });

    const built: MenuRow[] = [];
    const popular = sortItems(all.filter(({ item }) => item.best_seller || (Number(item.average_rating || item.weighted_rating) || 0) >= 4)).slice(0, 24);
    if (popular.length) built.push({ key: 'built-popular', title: 'Most Popular', icon: '/imgs/SVGs/Most Popular.svg', items: popular });

    const seenUnder = new Set<number>();
    const underFifteen = sortItems(all.filter(({ item }) => markedUpPrice(item) < 15)).filter(({ item }) => {
      if (seenUnder.has(item.id)) return false;
      seenUnder.add(item.id);
      return true;
    });
    if (underFifteen.length) built.push({ key: 'built-under-15', title: 'Under $15', icon: '/imgs/SVGs/store-duotone.svg', items: underFifteen });

    const dietaryOrder: { name: string; logo?: string }[] = [];
    const seen = new Set<string>();
    all.forEach(({ item }) => {
      (item.dietaries || []).forEach(dietary => {
        const name = dietary.name?.trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (seen.has(key)) return;
        if (key !== 'vegetarian' && key !== 'vegan' && dietary.enable_user_to_filter === false) return;
        seen.add(key);
        dietaryOrder.push({ name, logo: dietary.logo });
      });
    });
    dietaryOrder.sort((a, b) => {
      const rank = (name: string) => (name.toLowerCase() === 'vegetarian' ? 0 : name.toLowerCase() === 'vegan' ? 1 : 2);
      return rank(a.name) - rank(b.name) || a.name.localeCompare(b.name);
    });
    dietaryOrder.slice(0, 15).forEach(dietary => {
      const items = sortItems(all.filter(({ item }) => itemDietaries(item).includes(dietary.name.toLowerCase()))).slice(0, 24);
      if (items.length) built.push({ key: `built-${dietary.name.toLowerCase()}`, title: dietary.name, icon: dietary.logo, items });
    });

    const seenRestaurant = new Set<number>();
    const sections: MenuRow[] = [];
    groups.forEach(({ restaurant, menu }) => {
      (menu?.sections || []).forEach((section, index) => {
        const items = sortItems((section.fooditems || []).filter(item => accept(item, restaurant)).map(item => ({ item, restaurant })));
        if (!items.length) return;
        const first = !seenRestaurant.has(restaurant.address_id);
        seenRestaurant.add(restaurant.address_id);
        sections.push({
          key: `section-${restaurant.address_id}-${section.id || index}`,
          anchor: first ? `rest-${restaurant.address_id}` : undefined,
          title: section.name || 'Menu',
          eyebrow: restaurant.name,
          logo: restaurantMark(restaurant, menu),
          items
        });
      });
    });
    const visibleIds = new Set(all.map(entry => entry.item.id));
    return {
      built,
      sections,
      restaurantCount: seenRestaurant.size,
      itemCount: visibleIds.size
    };
  }, [restaurants, menus, budget, sort, query]);

  const subtotal = cart.lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
  const count = cart.lines.reduce((sum, line) => sum + line.quantity, 0);

  function comboAddressIds() {
    return new Set(restaurants.map(restaurant => restaurant.address_id));
  }

  function applyDayChange(line: CartLine | null) {
    const available = comboAddressIds();
    const kept = cart.lines.filter(item => available.has(item.restaurantAddressId));
    let lines = kept;
    if (line?.fooditemId) {
      const key = lineKey(line);
      const existing = kept.find(item => lineKey(item) === key);
      lines = existing
        ? kept.map(item => lineKey(item) === key ? { ...item, quantity: item.quantity + line.quantity, salesTaxRate: line.salesTaxRate ?? item.salesTaxRate } : item)
        : kept.concat(line);
    }
    setCart({ day: date, lines });
    setPendingAdd(null);
    if (line?.fooditemId) addedToCart(line);
  }

  function findMenuItem(fooditemId: number): { item: FoodItem; restaurant: Restaurant } | null {
    for (const menu of menus) {
      const restaurant = restaurants.find(entry => entry.address_id === menu.address_id || entry.name === menu.name);
      if (!restaurant) continue;
      for (const section of menu.sections || []) {
        const item = (section.fooditems || []).find(fooditem => fooditem.id === fooditemId);
        if (item) return { item, restaurant };
      }
    }
    return null;
  }

  function addSuggested(suggested: SuggestedLine[]) {
    const ready = suggested.filter(line => {
      if (line.needs_choices) return false;
      const found = findMenuItem(line.fooditem_id);
      return Boolean(found && meetsOptionRules(found.item, line.options));
    });
    if (ready.length) {
      setCart(current => {
        let next = current.lines;
        ready.forEach(line => {
          const found = findMenuItem(line.fooditem_id);
          if (line.replaces_fooditem_id) next = next.filter(item => item.fooditemId !== line.replaces_fooditem_id);
          const extras = (line.options || []).reduce((sum, option) => sum + (Number(option.price) || 0), 0);
          const cartLine: CartLine = {
            fooditemId: line.fooditem_id,
            name: line.name,
            price: found ? baseShown(found.item) + extras : Number(line.price) || 0,
            quantity: line.quantity,
            restaurantAddressId: line.restaurant_address_id,
            restaurantName: line.restaurant_name,
            image: found ? imageUrl(found.item.image) : undefined,
            salesTaxRate: found && found.item.sales_tax != null ? Number(found.item.sales_tax) || 0 : undefined,
            options: (line.options || []).map(option => ({
              optionset_id: option.optionset_id,
              option_id: option.option_id,
              name: option.name,
              price: Number(option.price) || 0
            }))
          };
          const key = lineKey(cartLine);
          const existing = next.find(item => lineKey(item) === key);
          next = existing
            ? next.map(item => lineKey(item) === key ? { ...item, quantity: item.quantity + line.quantity, salesTaxRate: cartLine.salesTaxRate ?? item.salesTaxRate } : item)
            : next.concat(cartLine);
        });
        return { day: date, lines: next };
      });
      const updating = ready.every(line => line.replaces_fooditem_id);
      toast(
        <div className="wo-added">
          <p>{updating ? 'Cart updated' : 'Suggested items added to cart'}</p>
          <button type="button" onClick={openCart}>
            View Cart <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
        </div>,
        { type: 'success' }
      );
    }
  }

  function addItem(line: CartLine) {
    if (cart.lines.length && cart.day && cart.day !== date) {
      const available = comboAddressIds();
      const removed = cart.lines.filter(item => !available.has(item.restaurantAddressId));
      if (removed.length) {
        setPendingAdd(line);
        return;
      }
    }
    setCart(current => {
      const key = lineKey(line);
      const existing = current.lines.find(item => lineKey(item) === key);
      const lines = existing
        ? current.lines.map(item => lineKey(item) === key ? { ...item, quantity: item.quantity + line.quantity, salesTaxRate: line.salesTaxRate ?? item.salesTaxRate } : item)
        : current.lines.concat(line);
      return { day: date, lines };
    });
    addedToCart(line);
  }

  function addedToCart(line: CartLine) {
    toast(
      <div>
        <p className="text-gray-primary fs-16 fw-normal m-0">{line.name} added to cart</p>
        <button type="button" className="link-style bg-transparent border-0 px-0" onClick={openCart}>
          View Cart <i className="fas fa-chevron-right ml-2 fs-16" aria-hidden="true" />
        </button>
      </div>,
      {
        icon: () => <img alt="food Item" className="rounded-lg cart-item-img" src={line.image || '/imgs/SVGs/ItemPlaceholder.svg'} />,
        type: 'success'
      }
    );
  }

  function removedNames() {
    const available = comboAddressIds();
    return Array.from(new Set(cart.lines.filter(item => !available.has(item.restaurantAddressId)).map(item => item.restaurantName)));
  }

  function placedFor(line: CartLine): PlacedItem {
    let found: FoodItem | undefined;
    menus.forEach(menu => {
      (menu.sections || []).forEach(section => {
        found = found || (section.fooditems || []).find(item => item.id === line.fooditemId);
      });
    });
    const restaurant = restaurants.find(entry => entry.address_id === line.restaurantAddressId) || {
      address_id: line.restaurantAddressId,
      restaurant_id: 0,
      name: line.restaurantName,
      position: 0,
      cuisine: '',
      rating: 0
    };
    return {
      item: found || { id: line.fooditemId, name: line.name, price: line.price, image: line.image },
      restaurant
    };
  }

  function replaceLine(original: CartLine, next: CartLine) {
    setCart(current => {
      const key = lineKey(original);
      const nextKey = lineKey(next);
      if (nextKey === key) {
        return { ...current, day: date, lines: current.lines.map(item => lineKey(item) === key ? { ...item, ...next } : item) };
      }
      const rest = current.lines.filter(item => lineKey(item) !== key);
      const match = rest.find(item => lineKey(item) === nextKey);
      const lines = match
        ? rest.map(item => lineKey(item) === nextKey ? { ...item, quantity: item.quantity + next.quantity, salesTaxRate: next.salesTaxRate ?? item.salesTaxRate } : item)
        : rest.concat(next);
      return { ...current, day: date, lines };
    });
  }

  function openCart() {
    if (count && cart.day && cart.day !== date && removedNames().length) {
      setPendingAdd({ fooditemId: 0, name: '', price: 0, quantity: 0, restaurantAddressId: 0, restaurantName: '' });
      return;
    }
    if (cart.day !== date) setCart(current => ({ ...current, day: date }));
    setCheckoutOpen(true);
  }

  const cutoff = cutoffCopy(date, timeZone);
  const averageMeal = count ? subtotal / count : 0;
  const restaurantFilters = restaurants.filter(restaurant => menuRows.sections.some(section => section.anchor === `rest-${restaurant.address_id}` || section.key.startsWith(`section-${restaurant.address_id}-`)));

  function scrollToSection(id: string) {
    const target = document.getElementById(id);
    if (!target) return;
    const tools = document.querySelector('.wo-tools');
    const offset = tools ? tools.getBoundingClientRect().bottom : 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset - 12;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  useEffect(() => {
    if (!selected && !editing && !pendingAdd && !checkoutOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (selected) setSelected(null);
      else if (editing) setEditing(null);
      else if (pendingAdd) setPendingAdd(null);
      else setCheckoutOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected, editing, pendingAdd, checkoutOpen]);

  if (!weekdayOrderAllowed()) {
    return (
      <div className="weekday-order">
        <Header />
        <div className="wo-success">
          <h2>Admins place orders here</h2>
          <p>This page is for company admins. You can order from your scheduled deliveries instead.</p>
          <a className="wo-primary" href="/menu">Go to your menu</a>
        </div>
      </div>
    );
  }

  return (
    <div className="weekday-order">
      <ToastContainer autoClose={3000} />
      <Header fromPublicMarketPlace={!signedIn()} />
      <div className="wo-tools">
        <div className="wo-bar">
          <div className="delivery-details-date wo-slot" ref={datePopRef}>
            <button type="button" className="icon-wrapper-large wo-icon-btn" aria-label="Change delivery time" onClick={() => setDateOpen(open => !open)}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </button>
            <div className="wo-slot-copy">
              <button type="button" className="delivery-details-text wo-slot-title" onClick={() => setDateOpen(open => !open)}>
                {deliveryLabel(date, windowStart, timeZone)} <i className="fas fa-chevron-down" />
              </button>
              <span className={cutoff.past ? 'badge-outline-danger' : 'badge-outline-primary'}>{cutoff.text}</span>
              {dateOpen && (
                <div className="wo-pop">
                  <label>Date
                    <input type="date" min={earliestDate(timeZone)} value={date} onChange={event => setDate(event.target.value)} />
                  </label>
                  <label>Window
                    <select value={windowStart} onChange={event => setWindowStart(event.target.value)}>
                      {WINDOWS.map(window => <option key={window.value} value={window.value}>{window.label}</option>)}
                    </select>
                  </label>
                </div>
              )}
            </div>
          </div>
          <div className="delivery-details-location wo-slot">
            <span className="icon-wrapper-large">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
              </svg>
            </span>
            <div className="wo-slot-copy">
              {savedAddresses.length > 1 && (
                <select
                  className="wo-address-select"
                  aria-label="Saved delivery addresses"
                  value={address?.id || 'new'}
                  onChange={event => {
                    if (event.target.value === 'new') {
                      setAddress(null);
                      if (addressInput.current) addressInput.current.value = '';
                      addressInput.current?.focus();
                      return;
                    }
                    const next = savedAddresses.find(row => row.id === Number(event.target.value));
                    if (next) chooseAddress(next);
                  }}
                >
                  {savedAddresses.map(row => <option key={row.id} value={row.id}>{row.address_line}</option>)}
                  <option value="new">Type a new address</option>
                </select>
              )}
              <input
                ref={addressInput}
                className="wo-address-input"
                placeholder="Delivery address"
                aria-label="Delivery address"
                onChange={event => {
                  const next = event.target.value;
                  setAddress(current => (current && current.address_line !== next ? null : current));
                }}
              />
            </div>
          </div>
          <div className="wo-cart-wrap">
            <button type="button" className="wo-cart-stats" onClick={openCart} aria-label="Open cart">
              <span><small>ITEMS</small><strong>{count}</strong></span>
              <span><small>AVG / MEAL</small><strong>{money(averageMeal)}</strong></span>
              <span><small>TOTAL</small><strong>{money(subtotal)}</strong></span>
            </button>
            <button type="button" className="wo-checkout" disabled={!count} onClick={openCart}>Checkout</button>
          </div>
        </div>
        <div className="wo-tools-inner">
          <HScroll chip>
            <div className="wo-jump">
              {loading && [0, 1, 2, 3, 4, 5].map(chip => <Skeleton key={chip} width={112} height={32} borderRadius={999} />)}
              {!loading && menuRows.built.map(row => (
                <button key={row.key} className="wo-filter" onClick={() => scrollToSection(row.key)}>
                  <FilterMark icon={row.icon} title={row.title} />
                  {row.title}
                </button>
              ))}
            </div>
          </HScroll>
        </div>
      </div>
      <main className={`wo-main${loading || restaurantFilters.length ? '' : ' wo-main-solo'}`}>
        {(loading || restaurantFilters.length > 0) && (
          <aside className="wo-gutter" aria-hidden={loading || undefined}>
            {loading && [0, 1, 2, 3, 4, 5, 6, 7].map(row => (
              <div className="wo-gutter-skel" key={row}>
                <Skeleton circle width={32} height={32} />
                <Skeleton width={118} height={14} />
              </div>
            ))}
            {!loading && restaurantFilters.map(restaurant => {
              const menu = menus.find(entry => entry.address_id === restaurant.address_id || entry.name === restaurant.name);
              return (
                <button key={restaurant.address_id} type="button" onClick={() => scrollToSection(`rest-${restaurant.address_id}`)}>
                  <img src={restaurantMark(restaurant, menu)} alt="" />
                  <span>{restaurant.name}</span>
                </button>
              );
            })}
          </aside>
        )}
        <div className="wo-body">
        <WeekdayOrderAssistant
          date={date}
          timeZone={timeZone}
          address={address}
          cart={cart.lines}
          budget={budget}
          onAddressSaved={id => setAddress(current => (current ? { ...current, id } : current))}
          onAdd={addSuggested}
          onOpenItem={line => {
            const found = findMenuItem(line.fooditem_id);
            if (found) setSelected({ ...found, preset: line });
          }}
        />
        <div className="wo-body-tools">
          {loading ? <Skeleton width={168} height={16} /> : <p className="wo-count">{menuRows.restaurantCount} {menuRows.restaurantCount === 1 ? 'restaurant' : 'restaurants'}, {menuRows.itemCount} {menuRows.itemCount === 1 ? 'item' : 'items'}</p>}
          <input
            className="wo-search"
            type="search"
            placeholder="Search"
            aria-label="Search menu"
            value={query}
            onChange={event => setQuery(event.target.value)}
          />
          <label className="wo-sort">Sort by
            <select value={sort} aria-label="Sort by" onChange={event => setSort(event.target.value as 'recommended' | 'rating' | 'price')}>
              <option value="recommended">Recommended</option>
              <option value="rating">Rating</option>
              <option value="price">Price</option>
            </select>
          </label>
          <label className="wo-range">
            <span className="wo-budget-label">Budget</span>
            <span className="wo-range-track">
              <input type="range" min={0} max={40} value={budget} aria-label="Budget" onChange={event => setBudget(Number(event.target.value))} />
            </span>
            <span>${budget}</span>
          </label>
        </div>
        {loading && <MenuSkeleton />}
        {error && <p className="wo-error">{error}</p>}
        {!loading && !error && menuRows.built.length + menuRows.sections.length === 0 && <p className="wo-empty">Nothing matches those filters.</p>}
        {!loading && [...menuRows.built, ...menuRows.sections].map(row => (
          <section className="wo-band" id={row.anchor || row.key} key={row.key}>
            <div className="wo-section-head">
              {row.eyebrow
                ? <img className="wo-rest-logo" src={row.logo || restaurantLogo} alt="" />
                : row.icon
                  ? <FilterMark icon={row.icon} title={row.title} className="wo-section-icon" />
                  : <span className="wo-section-badge">{row.title.slice(0, 1)}</span>}
              <div>
                {row.eyebrow && <div className="wo-rest-name">{row.eyebrow}</div>}
                <h2>{row.title}</h2>
              </div>
            </div>
            <HScroll photo>
              {groupSlides(row.items, false).map(slide => (
                <div className={slide.length > 1 ? 'wo-slide stack' : 'wo-slide'} key={slide.map(entry => entry.item.id).join('-')}>
                  {slide.map(({ item, restaurant }) => (
                    <button type="button" className="wo-card" key={`${row.key}-${item.id}`} onClick={() => setSelected({ item, restaurant })}>
                      {imageUrl(item.image) ? <div className="wo-card-img" style={{ backgroundImage: `url(${imageUrl(item.image)})` }} /> : null}
                      <div className="wo-card-body">
                        <div className="wo-card-top">
                          <div className="wo-card-name">
                            <h3>{item.name}</h3>
                            {!row.eyebrow && <div className="wo-from">from {restaurant.name}</div>}
                          </div>
                          <span className="wo-price">{cardPriceLabel(item)}</span>
                        </div>
                        <Stars rating={item.average_rating || item.weighted_rating} />
                        {item.description ? <p>{item.description}</p> : <p />}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </HScroll>
          </section>
        ))}
        </div>
      </main>
      {selected && (
        <ItemModal
          item={selected.item}
          restaurant={selected.restaurant}
          onClose={() => setSelected(null)}
          initial={selected.preset ? {
            quantity: selected.preset.quantity,
            options: (selected.preset.options || []).map(option => ({
              optionset_id: option.optionset_id,
              option_id: option.option_id,
              name: option.name,
              price: Number(option.price) || 0
            }))
          } : null}
          onAdd={line => { addItem(line); setSelected(null); }}
        />
      )}
      {pendingAdd && (
        <div className="wo-panel" onClick={() => setPendingAdd(null)}>
          <div className="wo-sheet" onClick={event => event.stopPropagation()}>
            <h2>Some items are not on this day</h2>
            <p>{removedNames().join(', ')} {removedNames().length === 1 ? 'is' : 'are'} not on this day’s menu and will be removed. Everything else stays in the cart.</p>
            <button className="wo-primary" onClick={() => { applyDayChange(pendingAdd.fooditemId ? pendingAdd : null); if (!pendingAdd.fooditemId) setCheckoutOpen(true); }}>Remove those and continue</button>
            <button className="wo-ghost" onClick={() => setPendingAdd(null)}>Keep browsing</button>
          </div>
        </div>
      )}
      {checkoutOpen && (
        <Elements stripe={stripePromise}>
          <CheckoutSheet
            cart={cart.day === date ? cart : { day: date, lines: cart.lines }}
            address={address}
            date={date}
            windowStart={windowStart}
            payBy={payBy}
            cutoffText={cutoff.text}
            cutoffPast={cutoff.past}
            onAddress={chooseAddress}
            onLines={lines => setCart(current => ({ ...current, day: date, lines }))}
            onEditLine={line => setEditing(line)}
            onClose={() => setCheckoutOpen(false)}
            onPlaced={next => { setReceipt(next); setCart({ day: '', lines: [] }); setCheckoutOpen(false); setPlaced(true); }}
          />
        </Elements>
      )}
      {editing && (
        <ItemModal
          item={placedFor(editing).item}
          restaurant={placedFor(editing).restaurant}
          editing={editing}
          onClose={() => setEditing(null)}
          onAdd={line => { replaceLine(editing, line); setEditing(null); }}
        />
      )}
      {placed && receipt && (
        <div className="wo-panel">
          <div className="wo-sheet wo-success">
            <h2>Order placed</h2>
            <p className="wo-receipt-when">{receipt.when}</p>
            <p className="wo-receipt-place">{receipt.location}</p>
            <p className="wo-receipt-note"><span>Instructions</span> {receipt.instructions || 'None'}</p>
            <p className="wo-receipt-note"><span>Items</span> {receipt.itemCount} {receipt.itemCount === 1 ? 'item' : 'items'}</p>
            <div className="wo-summary wo-receipt">
              <div className="wo-sum-row"><span>Food</span><span>{money(receipt.food)}</span></div>
              <div className="wo-sum-row"><span>Sales Tax</span><span>{money(receipt.salesTax)}</span></div>
              <div className="wo-sum-row"><span>Service fee (15%)</span><span>{money(receipt.service)}</span></div>
              <div className="wo-sum-row"><span>Delivery</span><span className="wo-free">Free</span></div>
              {receipt.cardFee > 0 && <div className="wo-sum-row"><span>Credit Card Processing Fee</span><span>{money(receipt.cardFee)}</span></div>}
              <div className="wo-sum-row wo-sum-total"><span>Total</span><span>{money(receipt.total)}</span></div>
            </div>
            <p className="wo-receipt-support">For any issues, contact <a href="mailto:support@chowmill.com">support@chowmill.com</a>.</p>
            <a className="wo-primary" href="/">Done</a>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckoutSheet({ cart, address, date, windowStart, payBy, cutoffText, cutoffPast, onAddress, onLines, onEditLine, onClose, onPlaced }: {
  cart: StoredCart;
  address: AddressPick | null;
  date: string;
  windowStart: string;
  payBy: 'invoice' | 'credit_card' | null;
  cutoffText: string;
  cutoffPast: boolean;
  onAddress: (next: AddressPick) => void;
  onLines: (lines: CartLine[]) => void;
  onEditLine: (line: CartLine) => void;
  onClose: () => void;
  onPlaced: (receipt: PlacedReceipt) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState<'cart' | 'checkout'>('cart');
  const [mode, setMode] = useState<'signup' | 'login'>('signup');
  const [accountReady, setAccountReady] = useState(signedIn);
  const [blocked, setBlocked] = useState(false);
  const [payMethod, setPayMethod] = useState(payBy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [instructions, setInstructions] = useState('');
  const [addressError, setAddressError] = useState('');
  const [addressTyped, setAddressTyped] = useState(false);
  const checkoutAddress = useRef<HTMLInputElement>(null);
  const onAddressRef = useRef(onAddress);
  onAddressRef.current = onAddress;
  const invoice = accountReady && payMethod === 'invoice';
  const charges = chargeSummary(cart.lines, !invoice);

  useEffect(() => {
    if (step !== 'checkout' || accountReady || !address) return undefined;
    const previous = localStorage.getItem('address');
    localStorage.setItem('address', JSON.stringify({
      address_line: address.address_line,
      city: address.city,
      state: address.state,
      zip: address.zip,
      latitude: address.latitude,
      longitude: address.longitude,
      time_zone: address.time_zone,
      suite_no: address.suite_no,
      street_number: address.street_number,
      street: address.street
    }));
    return () => {
      if (previous) localStorage.setItem('address', previous);
      else localStorage.removeItem('address');
    };
  }, [step, accountReady, address]);

  async function finishAuth() {
    if (!getloggedInUser().isCompanyAdmin) {
      setBlocked(true);
      return;
    }
    setAccountReady(true);
    try {
      const response = await axios.get('/billings', { headers: authHeaders() });
      const method = response.data?.invoice_credit_card;
      setPayMethod(method === 'invoice' ? 'invoice' : 'credit_card');
    } catch {
      setPayMethod('credit_card');
    }
  }

  useEffect(() => {
    if (address) {
      setAddressError('');
      return undefined;
    }
    return bindAddressInput(checkoutAddress, next => onAddressRef.current(next));
  }, [address]);

  async function cardToken() {
    if (!stripe || !elements) throw new Error('Card form is still loading.');
    const card = elements.getElement(CardElement);
    if (!card) throw new Error('Enter a card.');
    const result = await stripe.createToken(card);
    if (result.error || !result.token) throw new Error(result.error?.message || 'Card was declined.');
    return result.token.id;
  }

  async function place() {
    if (!charges.meetsMinimum) {
      setError(`Add ${money(charges.shortOfMinimum)} more to reach the $199 minimum.`);
      return;
    }
    if (!address) {
      const typed = checkoutAddress.current?.value.trim();
      setAddressError(typed
        ? 'Invalid address. Please be sure to select a valid & complete delivery address.'
        : 'Please enter delivery address.');
      checkoutAddress.current?.focus();
      return;
    }
    setBusy(true);
    setError('');
    try {
      const headers = authHeaders();
      const user = JSON.parse(localStorage.getItem('user') || '{}') as { address_id?: number; name?: string; first_name?: string; last_name?: string; phone_number?: string };
      const token = invoice ? undefined : await cardToken();
      const deliveryAt = deliveryInstant(date, windowStart, address.time_zone || 'America/Los_Angeles');
      const payload: Record<string, unknown> = {
        runningmenu: {
          runningmenu_name: 'Your Order',
          weekday_combo: true,
          delivery_at: deliveryAt,
          delivery_type: 'delivery',
          address_ids: Array.from(new Set(cart.lines.map(line => line.restaurantAddressId))),
          remove_previous_cart: false,
          driver_tip: 0,
          orders_attributes: cart.lines.map(line => ({
            quantity: line.quantity,
            fooditem_id: line.fooditemId,
            utensils: Boolean(line.utensils),
            remarks: '',
            optionsets_orders_attributes: Object.values((line.options || []).reduce<Record<number, { optionset_id: number; options_orders_attributes: { option_id: number }[] }>>((groups, option) => {
              const group = groups[option.optionset_id] || { optionset_id: option.optionset_id, options_orders_attributes: [] };
              group.options_orders_attributes.push({ option_id: option.option_id });
              groups[option.optionset_id] = group;
              return groups;
            }, {}))
          }))
        }
      };
      if (address.id) {
        (payload.runningmenu as { address_id: number }).address_id = address.id;
      } else {
        payload.company = { addresses_active_attributes: [address] };
      }
      const created = await axios.post(`/carts?delivery_at=${encodeURIComponent(deliveryAt)}`, payload, { headers });
      const slug = created.data.slug;
      const paidResponse = await axios.put(`/carts/${slug}/checkout`, {
        runningmenu: {
          person_name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim(),
          person_phone: user.phone_number || '',
          delivery_instructions: instructions.trim(),
          stripe_token: token
        }
      }, { headers });
      const paid = paidResponse.data || {};
      if (paid.success === false || (paid.status && paid.status !== 'approved' && paid.status !== 0)) {
        throw new Error(paid.message || 'The card was declined. The order was not placed.');
      }
      const food = Number(paid.items_subtotal);
      const salesTax = Number(paid.sales_tax);
      const cardFee = Number(paid.cc_processing_fee);
      const tipPaid = Number(paid.driver_tip);
      const total = Number(paid.total);
      const service = Math.round((total - food - salesTax - (Number(paid.delivery_cost) || 0) - tipPaid - cardFee) * 100) / 100;
      onPlaced({
        when: `${dayjs(date).format('dddd, MMM D')} · ${WINDOWS.find(window => window.value === windowStart)?.label || ''}`,
        location: paid.formatted_address || [address.address_line, address.city, address.state, address.zip].filter(Boolean).join(', '),
        instructions: instructions.trim(),
        itemCount: Number(paid.orders_quantity) || cart.lines.reduce((sum, line) => sum + line.quantity, 0),
        food: Number.isFinite(food) ? food : charges.subtotal,
        salesTax: Number.isFinite(salesTax) ? salesTax : charges.salesTax,
        service: Number.isFinite(service) ? service : charges.service,
        cardFee: Number.isFinite(cardFee) ? cardFee : (invoice ? 0 : charges.cardFee),
        total: Number.isFinite(total) ? total : charges.total
      });
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } }; message?: string }).response?.data?.message
        || (err as Error).message
        || 'Could not place the order.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  function changeQuantity(line: CartLine, next: number) {
    onLines(cart.lines.flatMap(item => {
      if (lineKey(item) !== lineKey(line)) return [item];
      return next > 0 ? [{ ...item, quantity: next }] : [];
    }));
  }

  function goCheckout() {
    if (!charges.meetsMinimum) return;
    if (!address) {
      const typed = checkoutAddress.current?.value.trim();
      setAddressError(typed
        ? 'Invalid address. Please be sure to select a valid & complete delivery address.'
        : 'Please enter delivery address.');
      checkoutAddress.current?.focus();
      return;
    }
    setStep('checkout');
  }

  if (step === 'cart') {
    return (
      <div className="wo-panel" onClick={onClose}>
        <div className="wo-sheet wo-cart" onClick={event => event.stopPropagation()}>
          <header className="wo-cart-head">
            <div className="wo-cart-brand">
              <span className="cart-logo d-flex align-items-center justify-content-center position-relative">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 33" style={{ height: 32, width: 28 }}>
                  <path d="M8,8V6A6,6,0,1,1,20,6V8h5a3,3,0,0,1,3,3V26a6,6,0,0,1-6,6H6a6,6,0,0,1-6-6V11A3,3,0,0,1,3,8ZM9,8H19V6A5,5,0,1,0,9,6ZM3,9a2,2,0,0,0-2,2V26a5,5,0,0,0,5,5H22a5,5,0,0,0,5-5V11a2,2,0,0,0-2-2H20v5.5a.5.5,0,1,1-1,0V9H9v5.5a.5.5,0,0,1-1,0V9Z" transform="translate(0.5 0.5)" fill="#dc3644" stroke="#dc3644" strokeWidth="1" />
                </svg>
                <span className="badge badge-danger badge-position rounded-pill position-absolute">{cart.lines.reduce((sum, line) => sum + line.quantity, 0)}</span>
              </span>
              <div className="wo-cart-copy">
                <p className="wo-cart-kicker">Your Cart from</p>
                <h2>Your Order</h2>
                {address && <p className="wo-cart-address"><i className="fas fa-map-marker-alt" /> {address.address_line}</p>}
                <span className={cutoffPast ? 'badge-outline-danger' : 'badge-outline-primary'}>{cutoffText}</span>
              </div>
            </div>
            <button className="wo-cart-close" onClick={onClose} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div className="wo-cart-items">
            {!address && (
              <div className="wo-cart-item wo-cart-address-slot">
                <span className="wo-address-mark" aria-hidden="true"><i className="fas fa-map-marker-alt" /></span>
                <div className="wo-line-copy">
                  <input
                    ref={checkoutAddress}
                    className="wo-address-input"
                    placeholder="Delivery address"
                    aria-label="Delivery address"
                    autoFocus
                    onChange={() => {
                      setAddressTyped(Boolean(checkoutAddress.current?.value.trim()));
                      setAddressError('');
                    }}
                  />
                  <span className={addressError ? 'wo-error' : 'wo-cart-address-hint'}>
                    {addressError || (addressTyped ? 'Select a suggested address to check out.' : 'Add your delivery address to check out.')}
                  </span>
                </div>
              </div>
            )}
            {!cart.lines.length && <p className="wo-empty">Your cart is empty.</p>}
            {cart.lines.map(line => (
              <div className="wo-cart-item" key={lineKey(line)}>
                <button type="button" className="wo-cart-open" onClick={() => onEditLine(line)}>
                  {line.image ? <img src={line.image} alt="" /> : <div className="wo-line-photo" />}
                  <div className="wo-line-copy">
                    <strong>{line.name}</strong>
                    {(line.options || []).length > 0 && <em className="wo-line-options">{line.options?.map(option => option.name).join(' · ')}</em>}
                    <span>{money(line.price)}</span>
                  </div>
                </button>
                <div className="wo-qty-control">
                  <button type="button" className="decrease-product-count" aria-label={line.quantity === 1 ? 'Remove item' : 'Decrease quantity'} onClick={() => changeQuantity(line, line.quantity - 1)}>
                    {line.quantity === 1 ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 16" className="wo-trash">
                        <path d="M4.225.553,4,1H1A1,1,0,0,0,1,3H13a1,1,0,0,0,0-2H10L9.775.553A1,1,0,0,0,8.881,0H5.119A1,1,0,0,0,4.225.553ZM13,4H1l.663,10.594A1.5,1.5,0,0,0,3.159,16h7.681a1.5,1.5,0,0,0,1.5-1.406Z" fill="#dc3644" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                      </svg>
                    )}
                  </button>
                  <span>{line.quantity}</span>
                  <button type="button" className="increase-product-count" aria-label="Increase quantity" onClick={() => changeQuantity(line, line.quantity + 1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          <footer className="wo-cart-foot">
            <div className="wo-cart-fees">
              <div className="wo-sum-row"><span>Items subtotal</span><span>{money(charges.subtotal)}</span></div>
              <div className="wo-sum-row"><span>Sales Tax</span><span>{money(charges.salesTax)}</span></div>
              <div className="wo-sum-row"><span>Service fee (15%)</span><span>{money(charges.service)}</span></div>
              <div className="wo-sum-row"><span>Delivery</span><span className="wo-free">Free</span></div>
              {!invoice && <div className="wo-sum-row"><span>Credit Card Processing Fee</span><span>{money(charges.cardFee)}</span></div>}
            </div>
            <div className="wo-cart-total">
              <h3>Total</h3>
              <h3>{money(charges.total)}</h3>
            </div>
            {charges.shortOfMinimum > 0 && <p className="wo-minimum">Add {money(charges.shortOfMinimum)} more to reach the $199 minimum.</p>}
            {!address && <p className="wo-minimum">Add a delivery address to check out.</p>}
            <button type="button" className="wo-checkout" disabled={!cart.lines.length || !address || !charges.meetsMinimum} onClick={goCheckout}>Checkout</button>
          </footer>
        </div>
      </div>
    );
  }

  if (blocked) {
    return (
      <div className="wo-panel" onClick={onClose}>
        <div className="wo-sheet wo-success" onClick={event => event.stopPropagation()}>
          <h2>Admins place orders here</h2>
          <p>This page is for company admins. You can order from your scheduled deliveries instead.</p>
          <a className="wo-primary" href="/menu">Go to your menu</a>
        </div>
      </div>
    );
  }

  if (!accountReady) {
    return (
      <div className="wo-panel" onClick={onClose}>
        <div className="wo-sheet wo-auth" onClick={event => event.stopPropagation()}>
          <header className="d-flex justify-content-between border-bottom p-4 cart-header">
            <div className="d-flex aling-items-center cart-summery">
              <button type="button" className="d-flex align-items-center justify-content-center rounded-circle border-0 cart-logo" onClick={() => mode === 'login' ? setMode('signup') : setStep('cart')} aria-label="Back">
                <svg xmlns="http://www.w3.org/2000/svg" width="28.67" height="24.572" viewBox="0 0 28.67 24.572">
                  <path d="M11.738,64.437.476,75.187a1.537,1.537,0,0,0,0,2.224l11.262,10.75a1.536,1.536,0,0,0,2.12-2.224l-8.491-8.1H27.134a1.536,1.536,0,1,0,0-3.072H5.371l8.491-8.1a1.539,1.539,0,0,0-2.124-2.227Z" transform="translate(0 -64.013)" fill="#dc3644" />
                </svg>
              </button>
              <div className="cart-summary-header-title pl-2 pl-sm-3">
                <p className="fs-16 fs-sm-12 fw-600 m-0 text-gray-100">To continue please</p>
                <h4 className="m-0 fw-600 fs-sm-16">{mode === 'login' ? 'Log In' : 'Signup'}</h4>
              </div>
            </div>
            <button className="wo-cart-close" onClick={onClose} aria-label="Close">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>
          <div className="wo-auth-body">
            {mode === 'login' ? (
              <Login
                isCartLogin
                isOpenModal={false}
                cartDisplayHandling={() => setMode('signup')}
                onAuthenticated={finishAuth}
              />
            ) : (
              <SignUpWidget
                isCartSignUp
                isOpenModal={false}
                cartDisplayHandling={() => setMode('login')}
                onAuthenticated={finishAuth}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wo-panel" onClick={onClose}>
      <div className="wo-sheet" onClick={event => event.stopPropagation()}>
        <div className="wo-sheet-head">
          <div>
            <button type="button" className="wo-continue" onClick={() => setStep('cart')}>Back to cart</button>
            <h2>Checkout</h2>
            <p className="wo-meta">{dayjs(date).format('dddd, MMM D')} · {WINDOWS.find(window => window.value === windowStart)?.label}</p>
          </div>
          <button className="wo-ghost" onClick={onClose} aria-label="Close">×</button>
        </div>
        {cart.lines.length > 0 && <div className="wo-summary">
          <div className="wo-sum-row"><span>Food</span><span>{money(charges.subtotal)}</span></div>
          <div className="wo-sum-row"><span>Sales Tax</span><span>{money(charges.salesTax)}</span></div>
          <div className="wo-sum-row"><span>Service fee (15%)</span><span>{money(charges.service)}</span></div>
          <div className="wo-sum-row"><span>Delivery</span><span className="wo-free">Free</span></div>
          {!invoice && <div className="wo-sum-row"><span>Credit Card Processing Fee</span><span>{money(charges.cardFee)}</span></div>}
          <div className="wo-sum-row wo-sum-total"><span>Total</span><span>{money(charges.total)}</span></div>
          {charges.shortOfMinimum > 0 && <p className="wo-minimum">Add {money(charges.shortOfMinimum)} more to reach the $199 minimum.</p>}
        </div>}
        {cart.lines.length > 0 && <label className="wo-instructions">
          Delivery Instructions
          <textarea
            value={instructions}
            rows={3}
            placeholder="e.g. Call receptionist upon arrival, wait in the lobby, setup in conference room, etc"
            onChange={event => setInstructions(event.target.value)}
          />
        </label>}
        {cart.lines.length > 0 && invoice && <p>Your company will be invoiced. No card is charged.</p>}
        {cart.lines.length > 0 && !invoice && <div className="wo-pay"><CardElement options={{ hidePostalCode: true }} /></div>}
        {error && <p className="wo-error">{error}</p>}
        {cart.lines.length > 0 && <button className="wo-primary" disabled={busy || !charges.meetsMinimum} aria-busy={busy} onClick={place}>{busy ? <><span className="wo-spinner" aria-hidden="true" /> Placing order…</> : invoice ? 'Place order' : 'Pay and place order'}</button>}
      </div>
    </div>
  );
}

function ItemModal({ item, restaurant, editing, initial, onClose, onAdd }: {
  item: FoodItem;
  restaurant: Restaurant;
  editing?: CartLine | null;
  initial?: { quantity?: number; options?: ChosenOption[] } | null;
  onClose: () => void;
  onAdd: (line: CartLine) => void;
}) {
  const [quantity, setQuantity] = useState(editing?.quantity || initial?.quantity || 1);
  const [utensils, setUtensils] = useState(Boolean(editing?.utensils));
  const [chosen, setChosen] = useState<Record<number, number[]>>(() => {
    const start: Record<number, number[]> = {};
    (editing?.options || initial?.options || []).forEach(option => {
      start[option.optionset_id] = (start[option.optionset_id] || []).concat(option.option_id);
    });
    return start;
  });
  const photo = imageUrl(item.image);
  const facts = (item.nutritions || []).filter(fact => fact.name && (fact.value != null || (fact.childs || []).some(child => child.name)));
  const rating = Number(item.average_rating || item.weighted_rating || 0);
  const stars = Math.round(Math.min(5, Math.max(0, rating)));
  const optionPrice = (item.optionsets || []).reduce((sum, set) => {
    const picked = chosen[set.id] || [];
    return sum + (set.options || []).reduce((inner, choice) => inner + (picked.includes(choice.id) ? Number(choice.price) || 0 : 0), 0);
  }, 0);
  const unit = markedUpPrice(item, optionPrice);
  const missingRequired = (item.optionsets || []).some(set => (chosen[set.id] || []).length < optionMinimum(set));

  function toggleOption(set: OptionSet, optionId: number) {
    const current = chosen[set.id] || [];
    const limit = set.end_limit && set.end_limit > 0 ? set.end_limit : 1;
    const next = limit <= 1
      ? [optionId]
      : current.includes(optionId)
        ? current.filter(id => id !== optionId)
        : current.length < limit ? current.concat(optionId) : current;
    setChosen({ ...chosen, [set.id]: next });
  }

  return (
    <div className="wo-panel center" onClick={onClose}>
      <div className="wo-modal" onClick={event => event.stopPropagation()}>
        <button className="wo-ghost" onClick={onClose} aria-label="Close">×</button>
        <div className="wo-modal-grid">
          <div className="wo-modal-side">
            <h2>{item.name}</h2>
            <div className="wo-stars" aria-label={`${rating} stars`}>{'★★★★★'.split('').map((star, index) => <span key={index} className={index < stars ? '' : 'empty'}>{star}</span>)}{item.rating_count ? <span className="wo-meta"> {item.rating_count}</span> : null}</div>
            <div className="wo-tags">
              {itemDietaries(item).map(name => <span className="wo-tag" key={name}>{name[0].toUpperCase() + name.slice(1)}</span>)}
            </div>
            {item.description && <p>{item.description}</p>}
            {photo ? <img className="wo-modal-photo" src={photo} alt="" /> : null}
          </div>
          <div>
            {(item.optionsets || []).length > 0 && (
              <div className="wo-acc">
                <strong>Additional options</strong>
                {item.optionsets?.map(set => (
                  <div key={set.id}>
                    <p>{set.name}{set.required ? ' · required' : ''}</p>
                    {(set.options || []).map(option => (
                      <label key={option.id} className="wo-option">
                        <input
                          type={(set.end_limit || 1) > 1 ? 'checkbox' : 'radio'}
                          name={`set-${set.id}`}
                          checked={(chosen[set.id] || []).includes(option.id)}
                          onChange={() => toggleOption(set, option.id)}
                        />
                        {option.description}{option.price ? ` +${money(Number(option.price))}` : ''}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
            <label className="wo-acc"><input type="checkbox" checked={utensils} onChange={event => setUtensils(event.target.checked)} /> Include utensils</label>
            {facts.length > 0 && (
              <details className="wo-acc">
                <summary>Nutritional Facts</summary>
                {facts.map(fact => (
                  <p key={fact.name}>{fact.name}: {fact.value}{(fact.childs || []).map(child => ` · ${child.name} ${child.value}`).join('')}</p>
                ))}
              </details>
            )}
          </div>
        </div>
        <div className="wo-qty">
          <div className="wo-stepper">
            <button type="button" className="wo-step" aria-label="Decrease quantity" onClick={() => setQuantity(value => Math.max(1, value - 1))}>
              <i className="fas fa-minus-circle fa-lg" />
            </button>
            <input
              value={quantity || ''}
              aria-label="Quantity"
              inputMode="numeric"
              onChange={event => {
                const next = event.target.value;
                if (next === '' || /^[0-9]+$/.test(next)) setQuantity(next === '' ? 0 : Number(next));
              }}
              onBlur={() => setQuantity(value => (!value || value < 1 ? 1 : Math.min(value, 99999)))}
            />
            <button type="button" className="wo-step" aria-label="Increase quantity" onClick={() => setQuantity(value => Math.min(99999, (value || 0) + 1))}>
              <i className="fas fa-plus-circle fa-lg" />
            </button>
          </div>
          <button
            className="wo-primary"
            disabled={missingRequired}
            onClick={() => onAdd({
              fooditemId: item.id,
              name: item.name,
              price: unit,
              quantity,
              restaurantAddressId: restaurant.address_id,
              restaurantName: restaurant.name,
              salesTaxRate: Number(item.sales_tax) || 0,
              image: imageUrl(item.image),
              utensils,
              options: (item.optionsets || []).flatMap(set => (set.options || [])
                .filter(choice => (chosen[set.id] || []).includes(choice.id))
                .map(choice => ({ optionset_id: set.id, option_id: choice.id, name: choice.description, price: Number(choice.price) || 0 })))
            })}
          >
            {editing ? 'Update order' : 'Add to Order'} · {money(unit * quantity)}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeekdayOrder;
